import { useEffect, useState } from 'react';
import { billingApi, type BillingData, type UserInfo } from '../services/api';

interface BillingProps {
  user: UserInfo;
}

export function Billing({ user }: BillingProps) {
  const [data, setData] = useState<BillingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const fetchBilling = async () => {
    try {
      setError(null);
      const res = await billingApi.get();
      setData(res);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBilling();
  }, []);

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      setSuccessMsg(null);
      const res = await billingApi.sync();
      setData(res);
      setSuccessMsg('Subscription status synced successfully from Sensifai Marketplace.');
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Sync failed. Could not reach Marketplace.');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  const sub = data?.subscription;
  const priceModel = sub?.priceModel;
  const balance = data?.balance?.balance;
  const marketplaceUrl = data?.marketplaceUrl || 'https://sensifai.com/marketplace/spi-yiujt6mkjrlfsgo7aanak';

  const formatPrice = () => {
    if (!priceModel) return null;
    if (priceModel.modelType === 'pay_as_go') {
      return priceModel.amount ? `€${priceModel.amount} / inspection unit` : 'Prepaid usage-based';
    }
    if (priceModel.modelType === 'tier_pay_as_go') {
      return 'Volume-based tiered pricing';
    }
    if (priceModel.amount) {
      const amountEur = (priceModel.amount / 100).toFixed(2);
      return `€${amountEur} / ${priceModel.interval || 'month'}`;
    }
    return 'Custom Pricing';
  };

  return (
    <>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>Billing & Subscription</h1>
          <p>Manage your SealVision subscription plan, prepaid balance, and Sensifai Marketplace billing.</p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="btn btn-secondary"
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          {syncing ? (
            <>
              <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
              Syncing...
            </>
          ) : (
            <>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              Refresh Status
            </>
          )}
        </button>
      </div>

      <div className="page-body">
        {error && (
          <div style={{ background: 'var(--status-fail-bg)', color: 'var(--status-fail)', padding: 12, borderRadius: 'var(--radius-md)', marginBottom: 20, border: '1px solid var(--status-fail)' }}>
            {error}
          </div>
        )}

        {successMsg && (
          <div style={{ background: 'var(--status-pass-bg)', color: 'var(--status-pass)', padding: 12, borderRadius: 'var(--radius-md)', marginBottom: 20, border: '1px solid var(--status-pass)' }}>
            {successMsg}
          </div>
        )}

        {/* No active subscription alert */}
        {(!data?.hasSubscription || !sub || sub.status === 'canceled' || sub.status === 'incomplete_expired') && (
          <div className="card" style={{ borderLeft: '4px solid var(--status-warning)', marginBottom: 24, background: '#fffdf5' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
              <div>
                <h3 style={{ margin: '0 0 6px 0', color: 'var(--text-primary)' }}>No Active Subscription</h3>
                <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  Your organization does not currently have an active SealVision subscription. To unlock unlimited inline inspection analysis and cloud edge telemetry, subscribe via the Sensifai Marketplace.
                </p>
              </div>
              <a
                href={marketplaceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
                style={{ whiteSpace: 'nowrap' }}
              >
                Subscribe on Marketplace &rarr;
              </a>
            </div>
          </div>
        )}

        <div className="grid-2">
          {/* Plan Details Card */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Current Plan</div>
              <div className="card-subtitle">Plan and entitlement details</div>
            </div>

            {sub ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                      {priceModel?.name || 'SealVision Enterprise'}
                    </div>
                    <div className="text-sm text-muted">
                      {priceModel?.modelType ? priceModel.modelType.replace(/_/g, ' ').toUpperCase() : 'SUBSCRIPTION'}
                    </div>
                  </div>
                  <span className={`badge ${sub.status === 'active' ? 'badge-pass' :
                      sub.status === 'trialing' ? 'badge-info' :
                        sub.status === 'past_due' ? 'badge-warning' :
                          sub.status === 'canceled' ? 'badge-fail' : 'badge-neutral'
                    }`} style={{ textTransform: 'capitalize', padding: '6px 12px', fontSize: '0.85rem' }}>
                    {sub.status}
                  </span>
                </div>

                <div style={{ padding: '12px 16px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)' }}>
                  <div className="text-sm text-muted">Pricing Model</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 600, marginTop: 4 }}>
                    {formatPrice()}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <div className="text-sm text-muted">Billing Cycle Start</div>
                    <div className="font-mono text-sm" style={{ marginTop: 2 }}>
                      {sub.currentPeriodStart ? new Date(sub.currentPeriodStart).toLocaleDateString() : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted">Renewal / End Date</div>
                    <div className="font-mono text-sm" style={{ marginTop: 2 }}>
                      {sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd).toLocaleDateString() : '—'}
                    </div>
                  </div>
                </div>

                {sub.trialEnd && (
                  <div style={{ padding: '8px 12px', background: 'var(--status-info-bg)', borderRadius: 'var(--radius-sm)', color: 'var(--status-info)', fontSize: '0.85rem' }}>
                    <strong>Trial Active:</strong> Ends on {new Date(sub.trialEnd).toLocaleDateString()}
                  </div>
                )}

                {sub.canceledAt && (
                  <div style={{ padding: '8px 12px', background: 'var(--status-fail-bg)', borderRadius: 'var(--radius-sm)', color: 'var(--status-fail)', fontSize: '0.85rem' }}>
                    Subscription canceled. Access will terminate at the end of the current billing cycle.
                  </div>
                )}
              </div>
            ) : (
              <div>
                <p className="text-muted text-sm" style={{ marginBottom: 16 }}>
                  No active plan found for this account. Subscribe on Sensifai Marketplace to activate your plan.
                </p>
                <a
                  href={marketplaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  View Plans on Sensifai Marketplace
                </a>
              </div>
            )}
          </div>

          {/* Prepaid Wallet & Usage Card */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Prepaid Wallet & Balance</div>
              <div className="card-subtitle">Pay-as-you-go metered usage credits</div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ padding: '16px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)' }}>
                <div className="text-sm text-muted">Customer Ledger Balance</div>
                <div style={{ fontSize: '1.8rem', fontWeight: 700, color: (balance !== undefined && balance !== null && balance <= 0) ? 'var(--status-fail)' : 'var(--accent-primary)', marginTop: 4 }}>
                  {balance !== undefined && balance !== null ? `€${Number(balance).toFixed(4)}` : '€0.0000'}
                </div>
                <div className="text-sm text-muted" style={{ marginTop: 4 }}>
                  {balance !== undefined && balance !== null && balance > 0
                    ? 'Balance is healthy. Metered inspection events are automatically charged.'
                    : 'Balance depleted or unconfigured. Top up your wallet on the marketplace.'}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                  <span className="text-muted">Metered Inspection Price:</span>
                  <span className="font-mono">€0.01 / inspection</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                  <span className="text-muted">Metering Gateway:</span>
                  <span>Sensifai Gatekeeper</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                  <span className="text-muted">Platform Entitlement:</span>
                  <span>
                    <span className={`badge ${data?.hasAccess ? 'badge-pass' : 'badge-warning'}`}>
                      {data?.hasAccess ? 'Access Active' : 'Access Restricted'}
                    </span>
                  </span>
                </div>
              </div>

              <div style={{ marginTop: 'auto', paddingTop: 8 }}>
                <a
                  href={marketplaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary"
                  style={{ width: '100%', textAlign: 'center', justifyContent: 'center' }}
                >
                  Top Up Wallet / Manage Payment
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Tiers if available */}
        {priceModel?.tiers && priceModel.tiers.length > 0 && (
          <div className="card mt-6">
            <div className="card-header">
              <div className="card-title">Volume Pricing Tiers</div>
              <div className="card-subtitle">Tiered Pay-as-you-go volume boundaries</div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Tier</th>
                    <th>Volume Up To</th>
                    <th>Unit Rate (€)</th>
                    <th>Flat Rate (€)</th>
                  </tr>
                </thead>
                <tbody>
                  {priceModel.tiers.map((tier, idx) => (
                    <tr key={idx}>
                      <td>Tier {idx + 1}</td>
                      <td>{tier.up_to ? tier.up_to.toLocaleString() : 'Unlimited'}</td>
                      <td>{tier.unit_amount_decimal ? `€${tier.unit_amount_decimal}` : tier.unit_amount ? `€${(tier.unit_amount / 100).toFixed(4)}` : 'Free'}</td>
                      <td>{tier.flat_amount ? `€${(tier.flat_amount / 100).toFixed(2)}` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Marketplace Integration Card */}
        <div className="card mt-6">
          <div className="card-header">
            <div className="card-title">Sensifai Marketplace Integration</div>
            <div className="card-subtitle">Authoritative subscription and identity management</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            <div>
              <div className="text-sm text-muted">Marketplace App ID</div>
              <div className="font-mono text-sm">01a066f1-954d-773f-aad3-25382cb280e1</div>
            </div>
            <div>
              <div className="text-sm text-muted">Customer Logto ID</div>
              <div className="font-mono text-sm">{user.user?.id ? user.user.id.substring(0, 16) : '—'}...</div>
            </div>
            <div>
              <div className="text-sm text-muted">Marketplace Product Listing</div>
              <div>
                <a
                  href={marketplaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm"
                  style={{ color: 'var(--accent-primary)', textDecoration: 'underline' }}
                >
                  SealVision on Sensifai Marketplace &rarr;
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
